import {
  PageBackground,
  PageShell,
  SectionTitle,
  ProductListPad,
  EmptySearch,
  Container,
  Form,
  FormTitle,
  ImageOnChef,
  ContainerPriceLeft,
  LabelPriceOldLeft,
  LabelPriceLeft,
  LabelPromotion,
} from "./styles";
import TopBar from "./components/TopBar";
import StoreHeader from "./components/StoreHeader";
import TaxasModal from "./components/TaxasModal";
import GroupTabs from "./components/GroupTabs";
import ProductCard from "./components/ProductCard";
import SizeCard from "./components/SizeCard";
import StoreSkeleton from "./components/StoreSkeleton";
import CartFooter from "./components/CartFooter";
import ProductSheet from "./components/sheets/ProductSheet";
import StepProductSheet from "./components/sheets/StepProductSheet";
import PizzaSheet from "./components/sheets/PizzaSheet";
import DesktopShell from "./components/desktop/DesktopShell";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect } from "react";
import LoadingOverlay from "../../components/ui/LoadingOverlay";
import ScrollToTopButton from "../../components/ui/ScrollToTopButton";
import { toast } from "react-toastify";
import {
  ICartProduct,
  IExtra,
  IProduct,
  ISize,
  IStep,
  IStepOption,
  IStore,
  IGroup,
} from "../../classes/interfaces";
import { useCart } from "../../hooks/useCart";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { auth } from "../../services/authService";
import {
  getStoreBySuffix,
  getGroupsForStore,
  getProductExtras,
  getProductSteps,
  getSizeProducts,
} from "../../services/storeService";
import { round2 } from "../../classes/numbers";
import { formatBRL } from "../../helpers/currency.helper";
import { calcExtrasTotal, calcProductTotal } from "../../utils/cartCalculations";
import { canAddToCart, isProductVisibleInChannel, StoreChannel } from "./helpers/productVisibility";

interface StoreProps {
  styleSale?: "menu";
}

export default function Store({ styleSale }: StoreProps) {
  const navigate = useNavigate();
  const { suffix, tableId } = useParams<{ suffix: string; tableId: string }>();
  const [state, setState] = React.useState({ status: "create", token: "" });
  const [company, setCompany] = React.useState<IStore>();
  const [groups, setGroups] = React.useState<IGroup[]>();
  const [modalProduct, setModalProduct] = React.useState<{
    open: boolean;
    product: IProduct | null;
  }>({ open: false, product: null });
  const [productsSize, setProductsSize] = React.useState<IProduct[]>([]);
  const [steps, setSteps] = React.useState<IStep[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [isTaxas, setIsTaxas] = React.useState(false);
  const [modalFracionado, setModalFracionado] = React.useState<{
    open: boolean;
    product: ICartProduct | null;
  }>({ open: false, product: null });
  const [query, setQuery] = React.useState("");

  function to(id: string) {
    const element = document.getElementById(id);
    if (element)
      element.scrollIntoView({
        behavior: "smooth",
      });
  }

  const { cart, setCart } = useCart();
  const isDesktopViewport = useIsDesktopViewport();

  const channel: StoreChannel = styleSale === "menu" ? "menu" : "store";
  // /menu e cardapio-vitrine: navega e abre detalhes, mas nao monta sacola.
  // Toda entrada na sacola (e o caminho ate ela) passa por aqui.
  const isMenu = !canAddToCart(channel);
  const channelGroups: IGroup[] | undefined = React.useMemo(() => {
    if (!groups) return groups;
    return groups
      .map((group) => ({
        ...group,
        products: group.products.filter((p) =>
          isProductVisibleInChannel(p.visibility, channel)
        ),
      }))
      .filter((group) => group.products.length > 0 || (group.sizes?.length ?? 0) > 0);
  }, [groups, channel]);

  const trimmedQuery = query.trim().toLowerCase();
  // Grupos vendidos por tamanho (pizza) chegam da API com products: [] — o card
  // exibido é o tamanho, e os sabores só são buscados quando o cliente toca nele.
  // Duas consequências para a busca:
  //  1. os tamanhos precisam ser filtrados também (pelo nome do grupo, já que o
  //     sabor não existe no cliente, e pelo nome/sigla do tamanho);
  //  2. os tamanhos são zerados nos grupos que têm produtos, porque a
  //     renderização usa `products.length > 0 ? produtos : tamanhos` — sem isso,
  //     uma busca que esvazia os produtos cai no fallback e exibe todos os
  //     tamanhos como se tivessem casado.
  // Com a busca ativa, grupos sem nenhum resultado saem da lista.
  const filteredGroups: IGroup[] = React.useMemo(() => {
    const matches = (text: string) => text.toLowerCase().includes(trimmedQuery);

    return (channelGroups ?? [])
      .map((group) => {
        const sizes = group.products.length === 0 ? group.sizes ?? [] : [];

        // Busca tambem pelos sabores vendidos por tamanho (ex.: "calabresa"
        // encontra a pizza). Como os sabores sao escolhidos dentro do modal do
        // tamanho, ao casar mantemos o grupo com todos os seus tamanhos.
        const flavorMatch = (group.flavorNames ?? []).some(matches);

        if (!trimmedQuery || matches(group.name) || flavorMatch)
          return { ...group, sizes };

        return {
          ...group,
          products: group.products.filter((p) =>
            matches(p.name + " " + (p.description || ""))
          ),
          sizes: sizes.filter((s) => matches(s.name + " " + s.initials)),
        };
      })
      .filter(
        (group) =>
          !trimmedQuery || group.products.length > 0 || group.sizes.length > 0
      );
  }, [channelGroups, trimmedQuery]);

  function qtyOf(productId: string) {
    return cart.products
      .filter((p) => p.product_id === productId)
      .reduce((sum, p) => sum + p.amount, 0);
  }

  function goToCartFromHeader() {
    if (isMenu) return;
    if (tableId) {
      navigate(`/store/${suffix}/table/${tableId}/cart`);
    } else {
      navigate(`/store/${suffix}/cart`);
    }
  }

  function goToCartFromFooter() {
    if (company?.status !== "opened") {
      toast("Ops... empresa está fechada no momento", { type: "error", autoClose: 3000 });
      return;
    }
    goToCartFromHeader();
  }

  function openModal(product: IProduct) {
    if (company?.status !== "opened") {
      toast("Ops... empresa está fechada no momento", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }
    if ((product.amount || 0) <= 0) {
      product.amount = 1;
    }
    const prod = {
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      promotionalPrice: product.promotionalPrice,
      obs: product.obs,
      imageUrl: product.imageUrl,
      amount: product.amount,
      step: product.step,
      extras: product.extras,
      total:
        product.promotionalPrice && product.promotionalPrice > 0
          ? product.promotionalPrice
          : product.price,
      isUnitKg: product.isUnitKg,
    };

    if (!product.extras || product.extras.length <= 0) {
      setLoading(true);
      getProductExtras(product.id)
        .then((extras) => {
          product.extras = extras;
          prod.extras = extras;
          setModalProduct({
            open: true,
            product: prod,
          });
        })
        .catch((error) => {
          toast(String(error), {
            type: "error",
            autoClose: 3000,
          });
        })
        .finally(() => setLoading(false));
    } else {
      //setLocalProduct(prod);
      //setIsOpen(true);
      setModalProduct({
        open: true,
        product: prod,
      });
    }
  }

  function closeModalProduct() {
    //setIsOpen(false);
    setModalProduct({
      open: false,
      product: null,
    });
  }

  function openTaxas() {
    setIsTaxas(true);
  }
  function closeTaxas() {
    setIsTaxas(false);
  }

  //ShowModal - steps
  const [localCartProduct, setLocalCartProduct] =
    React.useState<ICartProduct>();
  const [isSteps, setIsSteps] = React.useState(false);
  function openSteps(cartProduct: ICartProduct) {
    setLocalCartProduct(cartProduct);
    setSteps([]);

    let ids = "";
    let sizeId: string | undefined = undefined;

    if (cartProduct.flavours && cartProduct.flavours.length > 0) {
      cartProduct.flavours.forEach((flavour) => {
        if (ids === "") {
          ids = flavour.product_id;
        } else {
          ids += "," + flavour.product_id;
        }
      });

      if (localSize != null && localSize.id != null && localSize.id !== "") {
        sizeId = localSize.id;
      }
    } else {
      ids = cartProduct.product_id;
    }

    setLoading(true);
    getProductSteps(ids, sizeId)
      .then((stepsData) => {
        setSteps(stepsData);
        setIsSteps(true);
      })
      .catch((error) => {
        toast(String(error), {
          type: "error",
          autoClose: 3000,
        });
      })
      .finally(() => setLoading(false));
  }

  function closeSteps() {
    setIsSteps(false);
    setSteps([])
  }

  function moreStepOption(step: IStep, option: IStepOption) {
    if (step.maximum === 1) {
      if (option.amount === 1) {
        return;
      }
      step.options.forEach((op) => {
        op.amount = 0;
      });
      option.amount = 1;
      step.selected = 1;
    } else {
      if (option.maximum > 0 && option.amount >= option.maximum) {
        toast("Não é permitido mais desse item", {
          type: "error",
          autoClose: 3000,
        });
        return;
      }

      step.selected = 0;
      step.options.forEach((op) => {
        step.selected += op.amount;
      });

      if (step.maximum > 0) {
        if (step.selected >= step.maximum) {
          toast("Quantidade máxima excedida para essa etapa", {
            type: "error",
            autoClose: 3000,
          });
          return;
        }
      }

      option.amount += 1;
      step.selected += 1;
    }

    if (step.error === true) {
      if (step.selected >= step.minimum) {
        step.error = false;
      }
    }

    if (step.maximum > 0 && step.selected >= step.maximum) {
      try {
        const key = step.id.replaceAll("step", "");
        if (key !== "") {
          const id = "step" + (Number(key) + 1).toString();
          to(id);
        }
      } catch (error) {
        console.log(error);
      }
    }

    setState({ status: "increment", token: state.token });
  }

  function lessStepOption(step: IStep, option: IStepOption) {
    if (option.amount <= 0) {
      return;
    }

    if (step.minimum === 1 && step.maximum === 1) {
      return;
    }

    option.amount -= 1;

    step.selected = 0;
    step.options.forEach((op) => {
      step.selected += op.amount;
    });

    setState({ status: "decrement", token: state.token });
  }

  function calcTotalLocalProduct() {
    if (modalProduct.product !== null) {
      modalProduct.product.totalExtra = calcExtrasTotal(modalProduct.product.extras);
      modalProduct.product.total = calcProductTotal(modalProduct.product);
    }
  }

  function moreProduct() {
    const product = modalProduct.product!;
    const isUnitKg = product.isUnitKg;

    const step = isUnitKg ? 0.05 : 1;
    const currentAmount = product.amount ?? 0;

    const newAmount = currentAmount + step;

    product.amount = isUnitKg
      ? Number(newAmount.toFixed(2)) // mantém 1 casa decimal
      : Math.trunc(newAmount); // inteiro seguro

    calcTotalLocalProduct();
    setState({
      status: "moreProduct",
      token: state.token,
    });
  }

  function lessProduct() {
    const product = modalProduct.product!;
    const isUnitKg = product.isUnitKg;

    const step = isUnitKg ? 0.05 : 1;
    const minValue = step;

    const currentAmount = product.amount ?? step;

    if (currentAmount <= minValue) {
      return;
    }

    const newAmount = currentAmount - step;

    product.amount = isUnitKg
      ? Number(newAmount.toFixed(2)) // controla 1 casa decimal
      : Math.trunc(newAmount); // inteiro seguro

    calcTotalLocalProduct();
    setState({ status: "lessProduct", token: state.token });
  }

  function moreExtra(extra: IExtra) {
    if (extra.max > 0 && extra.amount >= extra.max) {
      toast("Não é permitido mais desse item", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }

    extra.amount += 1;
    calcTotalLocalProduct();
    setState({ status: "increment", token: state.token });
  }

  function lessExtra(extra: IExtra) {
    if (extra.amount <= 0) {
      return;
    }

    extra.amount -= 1;
    calcTotalLocalProduct();
    setState({ status: "decrement", token: state.token });
  }

  //ShowModal - size
  const [isSize, setIsSize] = React.useState(false);
  const [localSize, setSize] = React.useState({
    id: "",
    name: "",
    initials: "",
    fractioned: false,
    fractionalMaximum: 0.0,
    selected: 0,
    total: 0.0,
    obs: "",
    groupId: "",
    groupName: "",
  });

  function openSize(size: ISize) {
    size.selected = 0;
    size.total = 0;

    if (company?.status !== "opened") {
      toast("Ops... empresa está fechada no momento", {
        type: "error",
        autoClose: 3000,
      });
      return;
    }

    setSize(size);
    setLoading(true);
    getSizeProducts(size.id, size.groupId)
      .then((products) => {
        localSize.selected = 0;
        localSize.total = 0;
        setProductsSize(products);

        setIsSize(true);
      })
      .catch((error) => {
        toast(String(error), {
          type: "error",
          autoClose: 3000,
        });
      })
      .finally(() => setLoading(false));
  }

  function closeSize() {
    setIsSize(false);
  }

  const refreshStore = async (store: IStore) => {
    // Dois eixos distintos, propositalmente em campos separados:
    //
    // channel   = de ONDE o cliente entrou (a rota: /menu vs /store). Governa a
    //   navegacao da sacola. Derivar isso de store.styleSale fazia uma loja
    //   configurada como "menu" mandar o carrinho de volta para /menu mesmo
    //   quando aberta em /store (a "sacola voltando para o menu" da Gislaine).
    // styleSale = a CONFIGURACAO da loja (sale/share/menu). Governa qual botao
    //   de acao aparece no pagamento. Sobrescrever esse campo com o canal
    //   apagava o valor "sale" e sumia com o "Finalizar Compra" no /store.
    cart.channel = channel;
    cart.styleSale = store.styleSale ?? cart.styleSale;
    setCart(cart);
    sessionStorage.setItem("@menuchef/store", JSON.stringify(store));
    setCompany(store);

    try {
      const fetchedGroups = await getGroupsForStore(store.id);
      setGroups(fetchedGroups);
    } catch (error) {
      setState({ status: "404", token: state.token });
    }
  };

  const getStore = async () => {
    if (
      state.token !== "" &&
      (company === undefined || company === null) &&
      state.status !== "404"
    ) {
      setLoading(true);
      try {
        const store = await getStoreBySuffix(suffix!);
        await refreshStore(store);
      } catch (error) {
        setState({ status: "404", token: state.token });
      } finally {
        setLoading(false);
      }
    }
  };

  const getAuthorization = () => {
    if (state.token === "") {
      auth().then((token) => {
        if (token !== undefined && token !== "") {
          setState({ status: "allowed", token: token });
        }
      });
    }
  };

  useEffect(() => {
    getAuthorization();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional, see docs/superpowers/plans/2026-07-10-store-modularizacao.md Task 16
  }, []);

  useEffect(() => {
    if (state.token !== "") {
      getStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional, see docs/superpowers/plans/2026-07-10-store-modularizacao.md Task 16
  }, [state.token]);

  function moreProductSize(product: IProduct) {
    if (!localSize.fractioned) {
      localSize.selected = 0;
      productsSize.forEach((prodSize) => {
        prodSize.amount = 0;
      });
    }

    if (localSize.fractionalMaximum > 0) {
      if (localSize.selected >= localSize.fractionalMaximum) {
        toast("Limite máximo do tamanho atingido", {
          type: "error",
          autoClose: 3000,
        });
        return;
      }
    }

    if ((product.amount || 0) > 0) {
      if ((product.amount || 0) >= localSize.fractionalMaximum) {
        toast("Limite de sabores atingido", {
          type: "error",
          autoClose: 3000,
        });
        return;
      }
    }

    product.amount = (product.amount || 0) + 1;
    localSize.selected += 1;

    localSize.total = 0;
    if (company !== undefined && company !== null) {
      productsSize.forEach((prod: IProduct) => {
        if ((prod.amount || 0) > 0) {
          const price =
            prod.promotionalPrice && prod.promotionalPrice > 0
              ? prod.promotionalPrice
              : prod.price;
          if ((company?.fractionalPrice || "average") === "average") {
            localSize.total += round2(
              ((prod.amount || 0) * price) / localSize.selected
            );
          } else {
            if (localSize.total < price) {
              localSize.total = price;
            }
          }
        }
      });
    }

    setState({
      status: "more product",
      token: state.token,
    });
  }

  function lessProductSize(product: IProduct) {
    if ((product.amount || 0) <= 0) {
      return;
    }

    product.amount = (product.amount || 0) - 1;
    localSize.selected -= 1;

    localSize.total = 0;
    if (localSize.selected > 0) {
      productsSize.forEach((prod: IProduct) => {
        if ((prod.amount || 0) > 0) {
          const price =
            prod.promotionalPrice && prod.promotionalPrice > 0
              ? prod.promotionalPrice
              : prod.price;
          if ((company?.fractionalPrice || "average") === "average") {
            localSize.total += round2(
              ((prod.amount || 0) * price) / localSize.selected
            );
          } else {
            if (localSize.total < price) {
              localSize.total = price;
            }
          }
        }
      });
    }

    setState({
      status: "less product",
      token: state.token,
    });
  }

  function openModalFracionado(product: ICartProduct) {
    setModalFracionado({
      open: true,
      product: product,
    });
  }

  function closeModalFracionado() {
    setModalFracionado({
      open: false,
      product: null,
    });
  }

  // Voltar de estágio na tela única do PizzaSheet. Reusa exatamente os resets do
  // onClose escadeado: extras acumulados por onConfirmSteps são descartados para
  // não duplicarem na reconfirmação.
  function editPizzaFlavors() {
    if (localCartProduct) {
      localCartProduct.extras = [];
    }
    closeModalFracionado();
    closeSteps(); // stage volta a "flavors" — isSize continua true e productsSize preservado
  }

  function editPizzaSteps() {
    if (localCartProduct) {
      localCartProduct.extras = [];
    }
    closeModalFracionado(); // stage volta a "steps" — isSteps continua true e steps preservado
  }

  function addFracionado() {
    if (isMenu) return;
    const newProduct = modalFracionado.product!;

    cart.products.push(newProduct);
    cart.total += newProduct.total;

    steps.forEach((step) => {
      if (step.type === "product" && step.selected > 0) {
        step.options.forEach((option) => {
          if (option.amount > 0) {
            cart.grouping_last += 1;
            cart.product_last += 1;

            const newProduct: ICartProduct = {
              id: cart.product_last.toString(),
              grouping_id: (cart as any).grouping_id,
              product_id: option.id,
              amount: option.amount,
              product: option.name,
              price: option.price,
              imageUrl: option.imageUrl,
              obs: "",
              extras: [],
              totalProduct: option.amount * option.price,
              totalExtra: 0.0,
              total: option.amount * option.price,
            };
            cart.products.push(newProduct);
            cart.total += newProduct.total;
          }
        });
      }
    });

    setCart(cart);

    localSize.selected = 0;
    localSize.total = 0;
    setProductsSize([]);

    toast("Produto adicionado com sucesso", {
      type: "success",
      autoClose: 2000,
    });

    closeSize();
    closeSteps();
    setModalFracionado({
      open: false,
      product: null,
    });
  }

  if (state.status === "404") {
    return (
      <Container>
        <ImageOnChef />
        <Form>
          <FormTitle>Loja não encontrada :(</FormTitle>
          <div>
            Infelizmente não encontramos a loja solicitada. Confira se o link
            foi digitado corretamente ou tente novamente mais tarde.
          </div>
        </Form>
      </Container>
    );
  }

  if (company === undefined || company === null) {
    return (
      <PageBackground>
        <PageShell>
          <StoreSkeleton withHeader groups={2} />
        </PageShell>
      </PageBackground>
    );
  }

  const cartCount = cart.products.reduce((sum, p) => sum + (p.amount || 0), 0);

  // PizzaSheet renders one persistent sheet across 3 stages instead of the old
  // separate SizeModal/StepSelector/ModalFracionados dialogs. `isSteps` is shared
  // between the stand-alone stepped-product flow and the pizza-flavour-steps flow;
  // `localCartProduct.flavours` distinguishes which one is active.
  const isPizzaFlavourFlow = isSteps && !!localCartProduct?.flavours && localCartProduct.flavours.length > 0;
  const isStepOnlyFlow = isSteps && !isPizzaFlavourFlow;
  const pizzaSheetOpen = isSize || isPizzaFlavourFlow || modalFracionado.open;
  const pizzaSheetStage: "flavors" | "steps" | "extras" = modalFracionado.open
    ? "extras"
    : isPizzaFlavourFlow
    ? "steps"
    : "flavors";

  // Same handler reused for both the stand-alone stepped-product flow
  // (StepProductSheet) and the pizza-flavour-steps flow (PizzaSheet stage="steps"),
  // exactly as the original StepSelector's onConfirm was reused for both mounts.
  function onConfirmSteps() {
    if (isMenu) return;
    let errors = false;
    let page = "";
    let msg = "";
    steps.forEach((step) => {
      if (errors === false) {
        if (step.minimum > 0) {
          if (step.selected < step.minimum) {
            msg =
              "É necessário escolher no mínimo " +
              step.minimum +
              " ite" +
              (step.minimum === 1 ? "m" : "ns") +
              " da etapa " +
              step.name;
            errors = true;
            page = "step" + step.id;
            step.error = true;
          }
        }
      }
    });

    if (errors) {
      if (msg !== "") {
        toast(msg, {
          type: "error",
          autoClose: 3000,
        });
      }

      if (page !== "") {
        to(page);
      }

      setState({ status: "error", token: state.token });
    } else {
      let totalSteps = 0;
      steps.forEach((step) => {
        if (step.selected > 0) {
          if (step.type === "extra") {
            step.options.forEach((option) => {
              if (option.amount > 0) {
                const newExtra: IExtra = {
                  id: option.id,
                  amount: option.amount,
                  name: option.name,
                  price: option.price,
                  max: option.maximum,
                  stepId: step.id,
                  stepDescription: step.surname,
                };
                localCartProduct!.extras.push(newExtra);
                totalSteps += round2(newExtra.amount * newExtra.price);
              }
            });
          } else if (step.type === "custom") {
            step.options.forEach((option) => {
              if (option.amount > 0) {
                if (
                  localCartProduct!.obs === undefined ||
                  localCartProduct!.obs === null ||
                  localCartProduct!.obs === ""
                ) {
                  localCartProduct!.obs = `${option.amount}X ${option.name}`;
                } else {
                  localCartProduct!.obs += ` | ${option.amount}X ${option.name}`;
                }
              }
            });
          }
        }
      });

      if (localCartProduct) {
        if (localCartProduct.flavours) {
          openModalFracionado(localCartProduct);
        } else {
          localCartProduct.total += round2(
            localCartProduct.amount * totalSteps
          );
          cart.products.push(localCartProduct);
          cart.total += localCartProduct.total;

          steps.forEach((step) => {
            if (step.type === "product" && step.selected > 0) {
              step.options.forEach((option) => {
                if (option.amount > 0) {
                  cart.grouping_last += 1;
                  cart.product_last += 1;

                  const newProduct: ICartProduct = {
                    id: cart.product_last.toString(),
                    grouping_id: (cart as any).grouping_id,
                    product_id: option.id,
                    amount: option.amount,
                    product: option.name,
                    price: option.price,
                    imageUrl: option.imageUrl,
                    obs: "",
                    extras: [],
                    totalProduct: option.amount * option.price,
                    totalExtra: 0.0,
                    total: option.amount * option.price,
                  };
                  cart.products.push(newProduct);
                  cart.total += newProduct.total;
                }
              });
            }
          });

          setCart(cart);

          //localProduct.extras.map((extra) => {
          //if (extra.amount > 0) {
          //extra.amount = 0;
          //}
          //});

          localSize.selected = 0;
          localSize.total = 0;
          setProductsSize([]);

          toast("Produto adicionado com sucesso", {
            type: "success",
            autoClose: 2000,
          });

          closeSteps();
          closeSize();
          closeModalProduct();
        }
      }
    }
  }

  return (
    <>
        <LoadingOverlay show={loading} />

        <ProductSheet
          isOpen={modalProduct.open}
          product={modalProduct.product}
          onClose={closeModalProduct}
          readOnly={isMenu}
          onChangeObs={(obs) => {
            if (modalProduct.product !== null) {
              modalProduct.product.obs = obs;
            }
          }}
          onMoreProduct={moreProduct}
          onLessProduct={lessProduct}
          onMoreExtra={moreExtra}
          onLessExtra={lessExtra}
          onAdd={() => {
            const newExtras: IExtra[] = [];
            let total_extras = 0;

            if (isMenu || modalProduct.product === null) {
              return;
            }

            if (modalProduct.product.extras) {
              modalProduct.product.extras.forEach((extra) => {
                if (extra.amount > 0) {
                  const newExtra: IExtra = {
                    id: extra.id,
                    amount: extra.amount,
                    name: extra.name,
                    price: extra.price,
                    max: extra.max,
                  };
                  total_extras += round2(newExtra.amount * newExtra.price);
                  newExtras.push(newExtra);
                }
              });
            }

            cart.grouping_last += 1;
            cart.product_last += 1;

            const newProduct: ICartProduct = {
              id: cart.product_last.toString(),
              amount: modalProduct.product.amount || 0,
              extras: newExtras,
              product_id: modalProduct.product.id,
              product: modalProduct.product.name,
              obs: modalProduct.product.obs || "",
              price:
                modalProduct.product.promotionalPrice &&
                modalProduct.product.promotionalPrice > 0
                  ? modalProduct.product.promotionalPrice
                  : modalProduct.product.price,
              imageUrl: modalProduct.product.imageUrl,
              grouping_id: cart.grouping_last,
              totalProduct: 0,
              totalExtra: total_extras,
              total: 0,
            };

            newProduct.totalProduct = round2(
              newProduct.amount * newProduct.price
            );
            newProduct.total =
              newProduct.totalProduct +
              round2(newProduct.amount * newProduct.totalExtra);

            if (modalProduct.product.step) {
              openSteps(newProduct);
            } else {
              cart.products.push(newProduct);
              cart.total += newProduct.total;
              setCart(cart);

              if (modalProduct.product.extras) {
                modalProduct.product.extras.forEach((extra) => {
                  if (extra.amount > 0) {
                    extra.amount = 0;
                  }
                });
              }

              toast("Produto adicionado com sucesso", {
                type: "success",
                autoClose: 2000,
              });

              closeModalProduct();
              setState({ status: "new product", token: state.token });
            }
          }}
        />

        <StepProductSheet
          isOpen={isStepOnlyFlow}
          steps={steps}
          onMoreOption={moreStepOption}
          onLessOption={lessStepOption}
          onClose={() => {
            localCartProduct!.extras = [];
            closeSteps();
          }}
          onConfirm={onConfirmSteps}
        />

        <PizzaSheet
          open={pizzaSheetOpen}
          stage={pizzaSheetStage}
          localSize={localSize}
          productsSize={productsSize}
          onMoreFlavor={moreProductSize}
          onLessFlavor={lessProductSize}
          onChangeSizeObs={(obs) => {
            localSize.obs = obs;
          }}
          getPriceLeft={getPriceLeft}
          steps={isPizzaFlavourFlow ? steps : []}
          onMoreStepOption={moreStepOption}
          onLessStepOption={lessStepOption}
          onConfirmSteps={onConfirmSteps}
          fractionedProduct={modalFracionado.product}
          onConfirmExtras={addFracionado}
          onEditFlavors={() => {
            if (steps.some((step) => step.selected > 0)) {
              toast("Ao alterar os sabores, as etapas e os adicionais serão escolhidos novamente", {
                type: "info",
                autoClose: 3000,
              });
            }
            editPizzaFlavors();
          }}
          onEditSteps={editPizzaSteps}
          readOnly={isMenu}
          onClose={() => {
            if (modalFracionado.open) {
              if (localCartProduct && localCartProduct !== null) {
                localCartProduct!.extras = [];
              }
              closeModalFracionado();
            } else if (isPizzaFlavourFlow) {
              localCartProduct!.extras = [];
              closeSteps();
            } else {
              closeSize();
            }
          }}
          onConfirmFlavors={() => {
            if (isMenu) return;
            if (localSize.selected <= 0) {
              toast("É necessário selecionar no mínimo 1 item", {
                type: "error",
                autoClose: 2000,
              });
              return;
            }
            // GENERATORS
            cart.grouping_last += 1;
            cart.product_last += 1;

            const newProduct: ICartProduct = {
              id: cart.grouping_last.toString(),
              product_id: "",
              product: "",
              price: 0.0,
              grouping_id: (cart as any).grouping_id,
              sizeId: localSize.id,
              obs: localSize.obs,
              imageUrl: "",
              amount: 1,
              extras: [],
              totalProduct: 0.0,
              totalExtra: 0.0,
              total: 0.0,
            };
            const newProducts: ICartProduct[] = [];
            let existsSteps = false;

            productsSize.forEach((prod: IProduct) => {
              if ((prod.amount || 0) > 0) {
                if (!existsSteps) {
                  if (prod.step) {
                    existsSteps = prod.step;
                  }
                }

                const newProd: ICartProduct = {
                  id: cart.product_last.toString(),
                  amount: (prod.amount || 0) / localSize.selected,
                  extras: [],
                  product_id: prod.id,
                  product: prod.name,
                  obs: prod.obs || "",
                  price:
                    company.fractionalPrice === "bigger"
                      ? localSize.total
                      : prod.promotionalPrice && prod.promotionalPrice > 0
                      ? prod.promotionalPrice
                      : prod.price,
                  sizeId: localSize.id,
                  imageUrl: prod.imageUrl,
                  grouping_id: cart.grouping_last,
                  totalProduct: 0.0,
                  totalExtra: 0.0,
                  total: 0.0,
                };

                newProd.totalProduct = round2(newProd.amount * newProd.price);
                newProd.total =
                  newProd.totalProduct +
                  round2(newProd.amount * newProd.totalExtra);
                newProducts.push(newProd);

                // product
                let prefix = "";
                if ((prod.amount || 0) > 1) {
                  prefix =
                    (prod.amount || 0).toString() +
                    "/" +
                    localSize.selected +
                    " ";
                }

                if (newProduct.product === "") {
                  newProduct.product = prefix + newProd.product;
                } else {
                  newProduct.product += " | " + prefix + newProd.product;
                }

                if (newProduct.imageUrl === "" && newProd.imageUrl !== "") {
                  newProduct.imageUrl = newProd.imageUrl;
                }

                newProduct.totalProduct += newProd.totalProduct;
                newProduct.totalExtra += newProd.totalExtra;
                newProduct.total += newProd.total;
              }
            });

            newProduct.product =
              localSize.groupName +
              " " +
              localSize.name +
              " " +
              newProduct.product;
            newProduct.flavours = newProducts;

            if (existsSteps) {
              openSteps(newProduct);
            } else {
              openModalFracionado(newProduct);
            }
          }}
        />

        <TaxasModal isOpen={isTaxas} company={company} onClose={closeTaxas} />

        {isDesktopViewport ? (
          groups === undefined ? (
            <PageBackground>
              <PageShell>
                <StoreSkeleton withHeader groups={2} />
              </PageShell>
            </PageBackground>
          ) : (
            <DesktopShell
              company={company}
              tableId={tableId}
              groups={filteredGroups}
              allGroups={channelGroups}
              query={query}
              onQueryChange={setQuery}
              qtyOf={qtyOf}
              cartCount={cartCount}
              isMenuStyle={isMenu}
              onOpenProduct={openModal}
              onOpenSize={openSize}
              onOpenTaxas={openTaxas}
              onCart={goToCartFromHeader}
              onOrders={
                isMenu
                  ? undefined
                  : () => navigate(`/orders/${company.id}`)
              }
              onLogo={() => {
                setQuery("");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          )
        ) : (
          <PageBackground>
            <PageShell>
              <TopBar
                count={cartCount}
                onCart={isMenu ? undefined : goToCartFromHeader}
                onLogo={() => {
                  setQuery("");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />

              <StoreHeader company={company} groups={channelGroups} tableId={tableId} onOpenTaxas={openTaxas} isMenu={isMenu} />

              {/* chips seguem o resultado da busca (o rail do desktop já fazia
                  isso); durante o carregamento seguem indefinidos p/ o skeleton */}
              <GroupTabs
                groups={channelGroups && filteredGroups}
                query={query}
                onQueryChange={setQuery}
              />

              {groups === undefined ? (
                <StoreSkeleton groups={2} />
              ) : trimmedQuery && filteredGroups.length === 0 ? (
                <EmptySearch>Nenhum produto encontrado para essa busca.</EmptySearch>
              ) : (
                <div>
                  {filteredGroups.map((group) => (
                    <div id={"group" + group.id} key={group.id}>
                      <SectionTitle>{group.name}</SectionTitle>
                      <ProductListPad>
                        {group.products.length > 0
                          ? group.products.map((product: IProduct, index) => (
                              <ProductCard
                                key={product.id}
                                product={product}
                                qty={qtyOf(product.id)}
                                index={index}
                                onOpen={openModal}
                                readOnly={isMenu}
                              />
                            ))
                          : group.sizes?.map((size: ISize, index) => (
                              <SizeCard
                                key={size.id}
                                size={size}
                                index={index}
                                onOpen={(s) => {
                                  s.groupId = group.id;
                                  s.groupName = group.name;
                                  openSize(s);
                                }}
                              />
                            ))}
                      </ProductListPad>
                    </div>
                  ))}
                </div>
              )}
            </PageShell>
          </PageBackground>
        )}

      {!isDesktopViewport && (
        <CartFooter
          isMenuStyle={isMenu}
          count={cartCount}
          cartTotal={cart.total}
          onGoToCart={goToCartFromFooter}
        />
      )}

      {groups !== undefined && (
        <ScrollToTopButton
          offsetBottom={
            !isDesktopViewport && !isMenu && cartCount > 0 ? 100 : 24
          }
        />
      )}
    </>
  );
}

function getPriceLeft(product: IProduct) {
  if (product.promotionalPrice && product.promotionalPrice > 0) {
    return (
      <ContainerPriceLeft>
        <LabelPriceOldLeft>
          de {formatBRL(product.price)}
        </LabelPriceOldLeft>
        <LabelPriceLeft>
          por {formatBRL(product.promotionalPrice)}
        </LabelPriceLeft>
        <LabelPromotion>Promoção</LabelPromotion>
      </ContainerPriceLeft>
    );
  }
  return (
    <ContainerPriceLeft>
      <LabelPriceLeft>{formatBRL(product.price)}</LabelPriceLeft>
    </ContainerPriceLeft>
  );
}
